import * as XLSX from 'xlsx';
import { ContainerListItem } from '../../../_services/purchase-shipment.service';

// The Containers view's spreadsheet: the same columns as its table, one row
// per line in a container. Line ID ties a row back to its line on import.

export const LINE_COLUMNS = [
  'Line ID', 'Shipment', 'PO', 'SKU', 'Product', 'Incoterm', 'Quantity', 'Unit price', 'Subtotal',
  'Invoice date', 'Invoice no', 'Container', 'BL', 'ETD', 'ETA', 'POD', 'POL', 'Vessel', 'Shipping line', 'Status',
] as const;

const STATE_LABEL: Record<string, string> = {
  BOOKED: 'Booked', 'IN TRANSIT': 'In transit', ARRIVED: 'Arrived', CLEARED: 'Cleared',
  DELIVERED: 'Delivered', RECEIVED: 'Received', CANCELLED: 'Cancelled',
};

// A date as DD/MM/YYYY in the browser's own calendar (raw API dates can be
// ISO timestamps, which would otherwise shift a day).
function ukDate(v: string | null): string {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function writeContainerLinesSheet(lines: ContainerListItem[], fileName: string): void {
  const rows = lines.map((l) => ({
    'Line ID': l.id,
    Shipment: l.shipmentReference,
    PO: l.poReference ?? '',
    SKU: l.sku ?? '',
    Product: l.productName,
    Incoterm: l.incoterm ?? '',
    Quantity: l.quantity,
    'Unit price': l.unitPrice,
    Subtotal: l.subtotal,
    'Invoice date': ukDate(l.invoiceDate),
    'Invoice no': l.invoiceNumber ?? '',
    Container: l.containerNo ?? '',
    BL: l.billOfLading ?? '',
    ETD: ukDate(l.etd),
    ETA: ukDate(l.eta),
    POD: l.portOfDischarge ?? '',
    POL: l.portOfLoading ?? '',
    Vessel: l.vesselDetails ?? '',
    'Shipping line': l.shippingLine ?? '',
    Status: STATE_LABEL[l.status] ?? l.status,
  }));
  const sheet = XLSX.utils.json_to_sheet(rows, { header: [...LINE_COLUMNS] });
  sheet['!cols'] = LINE_COLUMNS.map((c) => ({ wch: c === 'Product' ? 44 : ['SKU', 'Vessel', 'Shipping line'].includes(c) ? 26 : c === 'Line ID' ? 8 : 14 }));
  const help = XLSX.utils.aoa_to_sheet([
    ['Updating container lines'],
    [''],
    ['Edit the cells and import the file back on Shipments > Containers. You will see every change before anything is saved.'],
    ['Line ID ties each row to its line - leave it as it is. Only existing lines can be updated here (add lines on the shipment).'],
    ['Line fields: Incoterm, Quantity, Unit price - change just that line.'],
    ['Container fields: Container (its number), Status - change the whole container, so keep them the same on every row of that container.'],
    ['Shipment fields: Invoice date, Invoice no, BL, ETD, ETA, POD, POL, Vessel, Shipping line - change the whole shipment; keep them the same on all its rows.'],
    ['PO, SKU, Product and Subtotal are for reference only.'],
    ['Status: Booked, In transit, Arrived, Cleared or Delivered. "Received" is only set by checking a container in at Incoming Stocks; received containers are never changed.'],
    ['Dates: DD/MM/YYYY. A blank cell leaves that value as it is.'],
  ]);
  help['!cols'] = [{ wch: 140 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Container lines');
  XLSX.utils.book_append_sheet(workbook, help, 'How to import');
  XLSX.writeFile(workbook, fileName);
}

// Any date cell (a real date, 17/10/2026 or 2026-10-17) as YYYY-MM-DD.
function isoDate(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  return s;
}

const text = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());

export async function readContainerLinesSheet(file: File): Promise<any[]> {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const sheet = workbook.Sheets['Container lines'] ?? workbook.Sheets[workbook.SheetNames[0]];
  const raw = sheet ? XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' }) : [];
  const norm = (h: string) => h.toLowerCase().replace(/[^a-z]/g, '');
  const column = new Map(LINE_COLUMNS.map((c) => [norm(c), c]));
  return raw
    .map((r, i) => {
      const o: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(r)) {
        const c = column.get(norm(k));
        if (c) o[c] = v;
      }
      return { o, row: i + 2 };
    })
    .filter(({ o }) => Object.values(o).some((v) => text(v) !== ''))
    .map(({ o, row }) => ({
      row,
      lineId: Number(o['Line ID']) || 0,
      incoterm: text(o['Incoterm']) || null,
      quantity: text(o['Quantity']) === '' ? null : Number(o['Quantity']),
      unitPrice: text(o['Unit price']) === '' ? null : Number(o['Unit price']),
      invoiceDate: isoDate(o['Invoice date']),
      invoiceNumber: text(o['Invoice no']) || null,
      containerNo: text(o['Container']) || null,
      billOfLading: text(o['BL']) || null,
      etd: isoDate(o['ETD']),
      eta: isoDate(o['ETA']),
      portOfDischarge: text(o['POD']) || null,
      portOfLoading: text(o['POL']) || null,
      vesselDetails: text(o['Vessel']) || null,
      shippingLine: text(o['Shipping line']) || null,
      status: text(o['Status']) || null,
    }));
}
